import { Country, Holder, HolderType, PhoneNumber } from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';

import { HolderDetailsForm } from '@/components/modules/KYC/KYCLoan/BusinessKYC/components/Holders/components/HolderDetailsForm';
import {
  HolderDetailsFormSchema,
  HolderDetailsFormType,
} from '@/components/modules/KYC/KYCLoan/BusinessKYC/components/Holders/schemas/HolderDetailsFormSchema';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { EmptyDialCodeOption } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/constants/EmptyDialCodeOption';
import { getDialCodeItemFromList } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/utils/getDialCodeItemFromList';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { removeNullKeysFromObject } from '@/utils/removeNullKeysFromObject';

interface HolderDetailsEditDialogProps {
  id: string;
  holder: Holder;
  holderType: HolderType;
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog: () => void;
  setHoldersToBusinessOnboardingDetails: (data) => void;
  loading?: boolean;
}

export const HolderDetailsEditDialog = ({
  id,
  holder,
  holderType,
  opened,
  setOpened,
  toggleDialog,
  setHoldersToBusinessOnboardingDetails,
  loading,
}: HolderDetailsEditDialogProps) => {
  const phoneNumber = holder.phoneNumber ? new PhoneNumber(holder.phoneNumber) : undefined;

  const holderDetailsDefaultValues = {
    firstName: holder.fullName.firstName,
    lastName: holder.fullName.lastName,
    birthDate: holder.birthDate,
    birthCountry: holder.birthCountry,
    nationality: holder.nationality,
    documentNumber: holder.documentNumber,
    expiration: holder.expiration,
    email: holder.email,
    dialCode: phoneNumber ? (getDialCodeItemFromList(phoneNumber.countryCallingCode) as Country) : EmptyDialCodeOption,
    phoneNumber: phoneNumber?.nationalNumber ?? '',
    grantingPowersDate: holder.grantingPowersDate,
    percentage: holder.percentage && Number(holder.percentage) * 100,
    relationshipWithOwner: holder.relationshipWithOwner,
  };

  const onSubmitForm = async (formValues: HolderDetailsFormType) => {
    const holders = {
      id,
      holders: [
        {
          ...formValues,
          phoneNumber: `+${formValues.dialCode?.dialCode}${formValues.phoneNumber}`,
          documentExpirationDate: formValues.expiration,
          ownershipPercentage: formValues.percentage && Number(formValues.percentage) / 100,
          relationshipWithOwner: formValues.relationshipWithOwner,
          type: holderType,
        },
      ],
    };
    await setHoldersToBusinessOnboardingDetails(removeNullKeysFromObject(holders));
    toggleDialog();
  };

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      DialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={`edit_${holderType}_details`}
      onSubmitForm={onSubmitForm}
      isLoading={loading}
      defaultValues={holderDetailsDefaultValues}
      formSchema={HolderDetailsFormSchema}
    >
      <HolderDetailsForm holder={holder} type={holderType} fieldVariant={FieldVariant.form} fullWidth />
    </EditDialogWrapper>
  );
};
